import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ExternalLink, GraduationCap } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import Pager from "../components/Pager";

export default function Courses() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [facets, setFacets] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [filters, setFilters] = useState({ q: "", category: searchParams.get("category") || "", delivery: "", level: "", online: "" });
  const [page, setPage] = useState(1);

  const load = (f = filters, p = page) => {
    const params = { page: p };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/courses", { params })
      .then((r) => { setData(r.data); setFacets(r.data.facets); })
      .catch((err) => { if (err?.response?.status === 404) setEnabled(false); });
  };

  useEffect(() => { load(); }, [page]);

  const apply = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setPage(1);
    load(next, 1);
  };

  if (!enabled) return null;

  const jsonLd = data && {
    "@context": "https://schema.org", "@type": "ItemList",
    name: "Courses & Skills on HiredToday",
    itemListElement: data.items.slice(0, 10).map((c, i) => ({
      "@type": "ListItem", position: i + 1,
      item: {
        "@type": "Course", name: c.title,
        description: c.summary || `${c.title}, ${c.provider}`,
        provider: { "@type": "Organization", name: c.provider || "Course provider" },
      },
    })),
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="courses-page">
      <Seo title="Courses & Skills, UK Training Matched to Real Jobs | HiredToday"
        description="Browse real UK courses, qualifications and training matched to live jobs. Free to explore, classroom, online and work-based options from the National Careers Service Course Directory."
        path="/courses" jsonLd={jsonLd} />

      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2 flex items-center gap-1.5">
          <GraduationCap size={14} /> Courses & Skills
        </p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-zinc-950">
          Skills that lead to real jobs
        </h1>
        <p className="text-base md:text-lg text-zinc-600 mt-3 leading-relaxed">
          Real UK courses and qualifications matched to the roles employers are hiring for right now.
          Explore, pick one, and come back stronger.
        </p>
      </div>

      <div className="mt-8 bg-white border border-zinc-200 rounded-lg p-4 flex flex-wrap gap-3 items-center" data-testid="courses-filters">
        <div className="flex-1 min-w-52 flex gap-2">
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && apply({})}
            placeholder="Search courses or providers…" data-testid="courses-search"
            className="flex-1 h-10 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
          <button onClick={() => apply({})} data-testid="courses-search-btn" aria-label="Search"
            className="h-10 px-3 bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors duration-200">
            <Search size={15} />
          </button>
        </div>
        <select value={filters.category} onChange={(e) => apply({ category: e.target.value })} data-testid="courses-filter-category"
          className="h-10 px-3 text-sm border border-zinc-300 rounded-md bg-white">
          <option value="">All subjects</option>
          {(facets?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.delivery} onChange={(e) => apply({ delivery: e.target.value })} data-testid="courses-filter-delivery"
          className="h-10 px-3 text-sm border border-zinc-300 rounded-md bg-white">
          <option value="">Any format</option>
          {(facets?.deliveries || []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filters.level} onChange={(e) => apply({ level: e.target.value })} data-testid="courses-filter-level"
          className="h-10 px-3 text-sm border border-zinc-300 rounded-md bg-white">
          <option value="">Any level</option>
          {(facets?.levels || []).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
          <input type="checkbox" checked={filters.online === "1"} data-testid="courses-filter-online"
            onChange={(e) => apply({ online: e.target.checked ? "1" : "" })} className="accent-zinc-900" />
          Online only
        </label>
      </div>

      {!data ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {[...Array(6)].map((_, i) => <div key={`course-skel-${i}`} className="h-44 rounded-lg bg-zinc-100 animate-pulse" />)}
        </div>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-zinc-400 mt-10 text-center" data-testid="courses-empty">
          No courses match those filters, try widening your search.
        </p>
      ) : (
        <>
          <p className="text-xs text-zinc-500 mt-6" data-testid="courses-count">{data.total.toLocaleString()} courses</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
            {data.items.map((c) => (
              <a key={c.id} href={c.url || "#"} target={c.url ? "_blank" : undefined} rel="noopener noreferrer"
                data-testid={`course-card-${c.id}`}
                className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-400 hover:shadow-sm transition-all duration-200 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{c.category}</span>
                  {c.online && <span className="text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 px-2 py-0.5 rounded">Online</span>}
                </div>
                <p className="font-display font-bold text-zinc-950 mt-3 leading-snug">{c.title}</p>
                <p className="text-xs text-zinc-500 mt-1">{c.provider}</p>
                <p className="text-xs text-zinc-400 mt-auto pt-3">
                  {[c.level && `Level ${c.level}`, c.delivery, c.location, c.cost].filter(Boolean).join(" · ") || "Details on the provider's site"}
                </p>
                {c.url && (
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
                    View course <ExternalLink size={12} />
                  </span>
                )}
              </a>
            ))}
          </div>
          <Pager page={page} pages={data.pages} onPage={setPage} />
        </>
      )}
      <p className="text-[11px] text-zinc-400 mt-10" data-testid="courses-attribution">
        Course data © Crown copyright, supplied by the National Careers Service Course Directory.
      </p>
    </main>
  );
}
